import { observer } from 'mobx-react';
import { FC, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { ControlledDater } from '@monorepo/controlled/src/components/controlled-dater/controlled-dater';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { Page, Pagebar, PagebarActions, PagebarTitle } from '@monorepo/base/src/components/page/page';
import {
	IPerformanceOptionsDateSegments,
	IPerformanceOptionsSortBy,
	IPerformanceOptionsSortOrder,
	PerformancesColumnsSnakeCase,
	groupByMapper,
} from '../../../modules/hooks/apis/use-reports-list';
import styles from './reports.page.module.scss';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useRoute } from '../../../modules/hooks/tools/use-route';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import { useReportActions } from '../../../modules/hooks/actions/report.actions';
import { useReportsList } from '../../../modules/hooks/apis/use-reports-list';
import { useReportHeaders } from '../../../modules/hooks/theaders/report.headers';
import { useDownload } from '@monorepo/tools/src/lib/hooks/tools/use-download';
import { HttpStore } from '@monorepo/controlled/src/stores/http.store';
import dayjs from 'dayjs';
import { DispatchLogsTypes } from '@monorepo/controlled/src/hooks/use-logs';
import { PerformanceApi } from '../../../modules/apis/performance.api';
import { Card } from '@monorepo/base/src/components/card/card';
import { Chart } from '@monorepo/base/src/components/chart/chart';
import { SpacerY } from '@monorepo/base/src/components/spacer/spacer';
import { useMetrics } from '../../../modules/hooks/tools/use-metrics';
import { availablePerformanceToMetrics } from '../../../modules/models/performance.model';
import React from 'react';
import { advertiserFilterPrototypes } from '../../../modules/components/filters-menu/filters';

export const Reports: FC = observer(() => {
	const { reportStore, toastsStore, settingsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const columnVisibility = usePageSettings().tableStore?.getColumnVisibility();
	const reportList = reportStore.getReportList();
	const metricsStore = reportStore.getMetrics();
	const [isLoadingCsv, setIsLoadingCsv] = useState<boolean>(false);
	const [showGraph, setShowGraph] = useState<boolean>(false);
	const [filtersReady, setFiltersReady] = useState<number>(0);
	const [isBlocking, setIsBlocking] = useState<boolean>(true);
	const [isBlockingGraph, setIsBlockingGraph] = useState<boolean>(true);
	const [data, setData] = useState<any[]>([]);
	const defaultSortBy = usePageSettings().tableStore?.getSortingBy();
	const groupByColumns = Object.keys(columnVisibility || {})
		.map((col: string) => {
			if (typeof col === 'string' && columnVisibility && columnVisibility[col]) {
				return groupByMapper[col];
			}
		})
		.filter(val => !!val) as PerformancesColumnsSnakeCase[];

	const filterStore = settingsStore
		.getPages()
		.get(currentRouteWithoutSpecialChars)
		?.tableStore.getFiltersStore(
			currentRouteWithoutSpecialChars === 'advertiserscreate' || currentRouteWithoutSpecialChars === 'advertisersedit'
				? 'advertisers'
				: currentRouteWithoutSpecialChars
		);
	const currentFilters = filterStore?.getCurrentFilters() || new Map();
	const DefaultAvailableFilters = [...advertiserFilterPrototypes];

	const { metrics, xLabels } = useMetrics(metricsStore.getData());

	const { fetchParams, fetchReports: fetchDataReports } = useReportsList(
		reportList,
		{
			groupBys: groupByColumns,
			reportName: 'adx',
			submit: false,

			sortBy: defaultSortBy?.[0]?.id || IPerformanceOptionsSortBy.Wins,
			sortOrder:
				defaultSortBy?.[0]?.desc !== undefined
					? defaultSortBy[0].desc
						? IPerformanceOptionsSortOrder.Desc
						: IPerformanceOptionsSortOrder.Asc
					: IPerformanceOptionsSortOrder.Desc,
			dateSegment: groupByColumns.includes(PerformancesColumnsSnakeCase.Dt)
				? IPerformanceOptionsDateSegments.Daily
				: IPerformanceOptionsDateSegments.None,
		},
		{ isStopUseEffect: isBlocking },
		toastsStore
	);
	useReportsList(
		metricsStore,
		{
			groupBys: [PerformancesColumnsSnakeCase.Dt],
			reportName: 'adx',
			dateSegment: IPerformanceOptionsDateSegments.Daily,
			submit: false,
		},
		{ isStopUseEffect: isBlockingGraph },
		toastsStore
	);
	const { columns } = useReportHeaders({ summary: reportList.getData()?.footer });
	useEffect(() => {
		if (filtersReady) {
			setIsBlocking(false);
			if (showGraph) {
				setIsBlockingGraph(false);
			}
		}
	}, [filtersReady]);

	useEffect(() => {
		if (!showGraph) {
			setIsBlockingGraph(true);
		} else {
			if (filtersReady) {
				setIsBlockingGraph(false);
			}
		}
	}, [showGraph]);

	useEffect(() => {
		if (reportList.getData()?.data) {
			setData(reportList.getData()?.data || []);
		}
	}, [reportList.getData()?.data]);

	useEffect(() => {
		if (reportList.getData()?.data && !isBlocking) {
			setIsBlocking(true);
		}
	}, [reportList.getData()?.data]);
	useEffect(() => {
		if (metricsStore.getData() && !isBlockingGraph) {
			setIsBlockingGraph(true);
		}
	}, [metricsStore.getData()]);

	const { FiltersActions } = useReportActions();

	const onDownloadReportFile = () => {
		const fileName = `adminx report ${dayjs(fetchParams.periodStart).format('YYYY/MM/DD')} - ${dayjs(fetchParams.periodEnd).format(
			'YYYY/MM/DD'
		)}`;
		const totalRows = data?.length || 0;
		if (totalRows <= 0) {
			toastsStore.addToast({
				msg: 'Cannot export empty file.',
				type: DispatchLogsTypes.Info,
			});
		} else {
			setIsLoadingCsv(true);
			useDownload({
				httpStore: new HttpStore<undefined, File | null>({
					httpFunc: () => PerformanceApi.downloadCsv({ ...fetchParams, limit: 100000 }).finally(() => setIsLoadingCsv(false)),
				}),
				fileName,
				extension: 'csv',
			});
		}
	};
	const teleportRootId = 'teleported-action-liner';

	return (
		<Page className={styles.reportsPage}>
			<Pagebar>
				<PagebarTitle>Self Service Reports</PagebarTitle>
				<PagebarActions>
					<ControlledDater />
				</PagebarActions>
			</Pagebar>
			<div className={styles.filtersStickyBar}>
				<div className={styles.filtersBar}>
					<FiltersActions
						availableFilters={DefaultAvailableFilters}
						currentFilters={currentFilters}
						clearAll={() => filterStore?.clearAll()}
						clearFilter={index => filterStore?.clearFilter(index)}
						addFilter={filter => filterStore?.addFilter(filter)}
						editFilter={(index, filter) => filterStore?.editFilter(index, filter)}
						IndexLinerLeftSide={undefined}
						IndexLinerRightSide={undefined}
					/>
					<div id={teleportRootId} className={styles.teleportContainer}>
						<div className={styles.graphToggleContainer}>
							<label htmlFor="graphToggler">Graph</label>
							<input id="graphToggler" type="checkbox" checked={showGraph} onChange={() => setShowGraph(prev => !prev)} />
						</div>
					</div>
				</div>
				<button className={styles.runBtn} onClick={() => setFiltersReady(prev => prev + 1)}>
					Run
				</button>
			</div>
			<div className={filtersReady == 0 ? styles.hidden : ''}>
				<React.Fragment>
					{showGraph && (
						<div className={styles.chartWrapper}>
							<Card full debugProps={{ dataAttrs: [new DataAttribute('id', 'chart')] }}>
								<Chart
									isLoading={metricsStore.getIsLoading()}
									metrics={metrics ?? []}
									labels={xLabels}
									legendOptions={Object.keys(availablePerformanceToMetrics)}
								/>
							</Card>
						</div>
					)}
					<SpacerY y={2} />
					<p className={styles.tableScrollHint}>Swipe horizontally to see all columns</p>
					<ControlledTable
						currentRoute={currentRouteWithoutSpecialChars}
						columns={columns}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'reports_table')] }}
						onExportToCsv={() => {
							onDownloadReportFile();
						}}
						data={data}
						isLoading={reportList.getIsLoading()}
						fetchReports={fetchDataReports}
						defaultSortBy={defaultSortBy?.[0] ? defaultSortBy : [{ id: IPerformanceOptionsSortBy.Wins, desc: true }]}
						isGlobalFilter={false}
						isLoadingCsv={isLoadingCsv}
						isSummary={true}
						hideFiltersActions={true}
						makeLinerPortable={true}
						teleportRootId={teleportRootId}
						blockRerenderOnFilterChange={true}>
						{() => {
							return {
								FiltersActions,
							};
						}}
					</ControlledTable>
				</React.Fragment>
			</div>
			<Outlet />
		</Page>
	);
});
